import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { applicationsAPI, assessmentsAPI, interviewsAPI } from '../lib/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

const FIT_COLORS = {
  Exceptional: { bg: 'rgba(5,150,105,0.08)', color: '#059669' },
  Strong: { bg: 'rgba(16,185,129,0.08)', color: '#10B981' },
  Good: { bg: 'rgba(30,104,255,0.08)', color: '#0076A3' },
  Maybe: { bg: 'rgba(245,158,11,0.08)', color: '#D97706' },
  Reject: { bg: 'rgba(239,68,68,0.08)', color: '#DC2626' },
  // legacy labels
  'Strong Fit': { bg: 'rgba(5,150,105,0.08)', color: '#059669' },
  'Good Fit': { bg: 'rgba(16,185,129,0.08)', color: '#10B981' },
  'Moderate Fit': { bg: 'rgba(245,158,11,0.08)', color: '#D97706' },
  'Weak Fit': { bg: 'rgba(239,68,68,0.08)', color: '#DC2626' },
  'Not a Fit': { bg: 'rgba(239,68,68,0.08)', color: '#DC2626' },
};

export default function ApplicationsPage() {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [interviewCandidate, setInterviewCandidate] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('technical');
  const [sending, setSending] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const res = await applicationsAPI.listAll();
      setApplications(res.data);
    } catch (err) {
      toast.error('Failed to load applications');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSendAssessment = async (app) => {
    try {
      const res = await assessmentsAPI.sendToCandidate(app.job_id, {
        candidate_name: app.candidate_name,
        candidate_email: app.candidate_email,
      });
      toast.success(`Assessment sent to ${app.candidate_name}! Link copied.`);
      navigator.clipboard.writeText(`${window.location.origin}${res.data.assessment_link}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send assessment');
    }
  };

  const handleSendInterview = async () => {
    if (!interviewCandidate) return;
    setSending(true);
    try {
      const res = await interviewsAPI.sendToCandidate(interviewCandidate.job_id, {
        candidate_name: interviewCandidate.candidate_name,
        candidate_email: interviewCandidate.candidate_email,
        template: selectedTemplate,
      });
      toast.success(`Interview invitation sent to ${interviewCandidate.candidate_name}! Link copied.`);
      navigator.clipboard.writeText(`${window.location.origin}${res.data.interview_link}`);
      setInterviewCandidate(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send interview');
    } finally { setSending(false); }
  };

  const getScoreColor = (s) => s >= 90 ? '#059669' : s >= 80 ? '#10B981' : s >= 70 ? '#0076A3' : s >= 60 ? '#D97706' : '#DC2626';

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#64748B' }}>Loading applications...</div>;

  // Group by type
  const selfApplied = applications.filter(a => a.type === 'self_applied');
  const uploaded = applications.filter(a => a.type === 'recruiter_uploaded');
  const filtered = filter === 'all' ? applications : filter === 'self_applied' ? selfApplied : uploaded;

  return (
    <div data-testid="applications-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Candidate Applications</h1>
          <p className="page-subtitle">{applications.length} candidates across all jobs</p>
        </div>
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
        {[
          { key: 'all', label: `All (${applications.length})` },
          { key: 'self_applied', label: `Self-Applied (${selfApplied.length})` },
          { key: 'recruiter_uploaded', label: `Uploaded (${uploaded.length})` },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{
            padding: '0.375rem 0.875rem', borderRadius: 999, fontSize: '0.8125rem', fontWeight: 500, cursor: 'pointer',
            border: 'none', background: filter === f.key ? '#0076A3' : '#F0EBE3',
            color: filter === f.key ? '#fff' : '#4A5568', transition: 'all 0.2s',
          }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Applications table */}
      {filtered.length === 0 ? (
        <div className="empty-state" style={{ paddingTop: '3rem' }}>
          <p>No applications yet. Candidates will appear here when they apply or you upload resumes.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {/* Header */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px 100px 80px 280px', gap: '1rem', padding: '0.625rem 1rem', fontSize: '0.6875rem', fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            <span>Candidate</span>
            <span>Job</span>
            <span>Source</span>
            <span>Score</span>
            <span style={{ textAlign: 'right' }}>Actions</span>
          </div>

          {filtered.map((app, i) => {
            const fitCfg = FIT_COLORS[app.fit] || { bg: '#F1EAE1', color: '#64748B' };
            return (
              <Card key={`${app.job_id}-${app.candidate_email}-${i}`} style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 12, padding: '0.875rem 1rem' }} className="candidate-row">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px 100px 80px 280px', gap: '1rem', alignItems: 'center' }}>
                  {/* Candidate */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #0076A3, #00A0E0)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8125rem', fontWeight: 600, color: '#fff', flexShrink: 0 }}>
                      {app.candidate_name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#002A38', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{app.candidate_name}</div>
                      <div style={{ fontSize: '0.6875rem', color: '#94A3B8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{app.candidate_email}</div>
                    </div>
                  </div>

                  {/* Job */}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '0.8125rem', fontWeight: 500, color: '#002A38', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{app.job_title}</div>
                    <div style={{ fontSize: '0.6875rem', color: '#94A3B8' }}>{app.job_location}</div>
                  </div>

                  {/* Source */}
                  <Badge style={{
                    background: app.type === 'self_applied' ? 'rgba(16,185,129,0.08)' : 'rgba(30,104,255,0.08)',
                    color: app.type === 'self_applied' ? '#059669' : '#0076A3',
                    borderRadius: 999, fontSize: '0.5625rem', border: 'none', justifySelf: 'start',
                  }}>
                    {app.type === 'self_applied' ? 'Applied' : 'Uploaded'}
                  </Badge>

                  {/* Score */}
                  <div>
                    {app.score != null ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                        <span style={{ fontFamily: 'JetBrains Mono', fontSize: '0.9375rem', fontWeight: 700, color: getScoreColor(app.score) }}>{app.score}</span>
                      </div>
                    ) : (
                      <span style={{ fontSize: '0.75rem', color: '#CBD5E1' }}>---</span>
                    )}
                    {app.fit && (
                      <span style={{ fontSize: '0.5625rem', color: fitCfg.color }}>{app.fit}</span>
                    )}
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '0.375rem', justifyContent: 'flex-end' }}>
                    <button onClick={() => handleSendAssessment(app)} title="Send Assessment"
                      style={{ padding: '0.3125rem 0.625rem', borderRadius: 6, border: '1px solid rgba(124,58,237,0.2)', background: 'rgba(124,58,237,0.06)', cursor: 'pointer', fontSize: '0.6875rem', fontWeight: 600, color: '#0076A3', whiteSpace: 'nowrap' }}>
                      Assessment
                    </button>
                    <button onClick={() => { setInterviewCandidate(app); setSelectedTemplate('technical'); }} title="Send Interview"
                      style={{ padding: '0.3125rem 0.625rem', borderRadius: 6, border: '1px solid rgba(43,108,176,0.2)', background: 'rgba(43,108,176,0.06)', cursor: 'pointer', fontSize: '0.6875rem', fontWeight: 600, color: '#0076A3', whiteSpace: 'nowrap' }}>
                      Interview
                    </button>
                    {app.candidate_id && (
                      <button onClick={() => navigate(`/candidates/${app.candidate_id}`)} title="View Profile"
                        style={{ padding: '0.3125rem 0.625rem', borderRadius: 6, border: '1px solid #CBD5E1', background: '#FAF7F2', cursor: 'pointer', fontSize: '0.6875rem', fontWeight: 500, color: '#64748B', whiteSpace: 'nowrap' }}>
                        View
                      </button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Send Interview Dialog */}
      <Dialog open={!!interviewCandidate} onOpenChange={() => setInterviewCandidate(null)}>
        <DialogContent style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, maxWidth: 440 }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cabinet Grotesk' }}>Send Interview Invitation</DialogTitle>
          </DialogHeader>
          {interviewCandidate && (
            <div>
              <div style={{ padding: '0.875rem', borderRadius: 10, background: '#F1EAE1', border: '1px solid #ECE7DE', marginBottom: '1.25rem' }}>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#002A38' }}>{interviewCandidate.candidate_name}</div>
                <div style={{ fontSize: '0.75rem', color: '#64748B' }}>{interviewCandidate.candidate_email} &middot; {interviewCandidate.job_title}</div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#64748B', marginBottom: '0.5rem' }}>Interview Type</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {[
                    { key: 'technical', label: 'Technical', desc: 'System design, coding, technical depth' },
                    { key: 'behavioral', label: 'Behavioral', desc: 'Teamwork, leadership, soft skills' },
                    { key: 'situational', label: 'Situational', desc: 'Decision-making, real-world scenarios' },
                  ].map(t => (
                    <button key={t.key} onClick={() => setSelectedTemplate(t.key)} style={{
                      display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', borderRadius: 10,
                      border: `1px solid ${selectedTemplate === t.key ? '#0076A3' : '#ECE7DE'}`,
                      background: selectedTemplate === t.key ? 'rgba(43,108,176,0.04)' : '#fff',
                      cursor: 'pointer', textAlign: 'left', width: '100%',
                    }}>
                      <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${selectedTemplate === t.key ? '#0076A3' : '#CBD5E1'}`, background: selectedTemplate === t.key ? '#0076A3' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {selectedTemplate === t.key && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                      </div>
                      <div>
                        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#002A38' }}>{t.label}</div>
                        <div style={{ fontSize: '0.6875rem', color: '#94A3B8' }}>{t.desc}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setInterviewCandidate(null)} style={{ borderRadius: 10 }}>Cancel</Button>
            <Button onClick={handleSendInterview} disabled={sending} style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontWeight: 600 }}>
              {sending ? 'Sending...' : 'Send Interview Invitation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
